// utils/ExportUtils.js
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

class ExportUtils {
    /**
     * Export a single lead to CSV and trigger download
     * @param {Object} lead - Lead object to export
     */
    static exportSingleLead(lead) {
        if (!lead) {
            console.error('No lead provided for export');
            return;
        }

        // Format lead data for CSV
        const leadData = this.formatLeadForExport(lead);

        // Convert to CSV and download
        this.downloadCSV([leadData], `Lead_${lead._id || 'export'}.csv`);
    }

    /**
     * Export a single lead to Excel and trigger download
     * @param {Object} lead - Lead object to export
     */
    static exportSingleLeadToExcel(lead) {
        if (!lead) {
            console.error('No lead provided for export');
            return;
        }

        // Format lead data for Excel
        const leadData = this.formatLeadForExport(lead);

        // Convert to Excel and download
        this.downloadExcel([leadData], `Lead_${lead._id || 'export'}.xlsx`);
    }

    /**
     * Export multiple leads to CSV and trigger download
     * @param {Array} leads - Array of lead objects to export
     * @param {string} filename - Optional custom filename
     */
    static exportMultipleLeads(leads, filename = 'Leads_Export.csv') {
        if (!leads || !leads.length) {
            console.error('No leads provided for export');
            return;
        }

        // Format each lead for CSV export
        const formattedLeads = leads.map(lead => this.formatLeadForExport(lead));

        // Convert to CSV and download
        this.downloadCSV(formattedLeads, filename);
    }

    /**
     * Export multiple leads to Excel and trigger download
     * @param {Array} leads - Array of lead objects to export
     * @param {string} filename - Optional custom filename
     */
    static exportMultipleLeadsToExcel(leads, filename = 'Leads_Export.xlsx') {
        if (!leads || !leads.length) {
            console.error('No leads provided for export');
            return;
        }

        // Format each lead for Excel export
        const formattedLeads = leads.map(lead => this.formatLeadForExport(lead));

        // Convert to Excel and download
        this.downloadExcel(formattedLeads, filename);
    }

    /**
     * Format a lead object for CSV export
     * @param {Object} lead - Lead object to format
     * @returns {Object} Formatted lead data for CSV
     */
    static formatLeadForExport(lead) {
        const formattedLead = {
            'Name': lead.leadName || '',
            'Email': lead.leadEmail || '',
            'Phone Number': lead.leadPhoneNumber || '',
            'Job Title': lead.leadJobTitle || '',
            'Company': lead.leadCompany || '',
            'Notes': lead.leadNotes || '',
            'Source': lead.leadSource || '',
            'Date Added': lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '',
        };

        // Process custom fields if present
        if (lead.customFields && typeof lead.customFields === 'object') {
            // Handle both Map and regular object structures
            if (lead.customFields instanceof Map) {
                // Handle Map structure
                for (const [key, value] of lead.customFields.entries()) {
                    const fieldValue = typeof value === 'object' ? value.value : value;
                    formattedLead[key] = fieldValue || '';
                }
            } else {
                // Handle regular object structure
                Object.entries(lead.customFields).forEach(([key, field]) => {
                    const fieldValue = typeof field === 'object' ? field.value : field;
                    const fieldLabel = typeof field === 'object' && field.label ? field.label : key;
                    formattedLead[fieldLabel] = fieldValue || '';
                });
            }
        }

        return formattedLead;
    }

    /**
     * Convert data to CSV and trigger download
     * @param {Array} data - Array of objects to convert to CSV
     * @param {string} filename - Name of the file to download
     */
    static downloadCSV(data, filename) {
        try {
            // Convert data to CSV string
            const csv = Papa.unparse(data, {
                quotes: true, // Quote all fields
                quoteChar: '"',
                delimiter: ',',
                header: true,
                newline: '\r\n'
            });

            // Create a blob with the CSV data
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            // Create a download link
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';

            // Add link to document, click it, then remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL.createObjectURL
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 100);

            return true;
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            return false;
        }
    }

    /**
     * Convert data to Excel and trigger download
     * @param {Array} data - Array of objects to convert to Excel
     * @param {string} filename - Name of the file to download
     */
    static downloadExcel(data, filename) {
        try {
            // Create a new workbook
            const workbook = XLSX.utils.book_new();

            // Convert data to worksheet
            const worksheet = XLSX.utils.json_to_sheet(data);

            // Add the worksheet to the workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

            // Create a blob with the Excel data
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);

            // Create a download link
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.visibility = 'hidden';

            // Add link to document, click it, then remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL.createObjectURL
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 100);

            return true;
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            return false;
        }
    }
}

export default ExportUtils;